import ContentSaveIcon from '@mui/icons-material/Save';
import { Button as MuiButton, Stack } from '@mui/material';
import { FormEvent, useEffect } from 'react';
import {
    AutocompleteInput,
    RecordContextProvider,
    ReferenceInput,
    Toolbar,
    useNotify,
} from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import {
    memberInputText,
    memberOptionText,
} from '@app/member/MemberInputOption';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import {
    createTask,
    deleteTask,
    updateTask,
    type MemberId,
    type Task,
    type TaskEditionInput,
    type TaskType,
} from '@wwf/sdk';
import { DateInput, SelectInput, TextInput, required } from 'react-admin';

type DrawerMode = 'create' | 'edit';

type TaskFormValues = {
    text: string;
    assignedMemberId: string | null;
    dueDate: string;
    type: string;
};

const defaultValues: TaskFormValues = {
    text: '',
    assignedMemberId: null,
    dueDate: new Date().toISOString().slice(0, 10),
    type: 'None',
};

export function TaskDrawerForm({
    mode,
    record,
    loading,
    initialAssignedMemberId,
    onClose,
    onSuccess,
}: {
    mode: DrawerMode;
    record?: Task | null;
    loading: boolean;
    initialAssignedMemberId?: string | null;
    onClose(): void;
    onSuccess(): void;
}) {
    const notify = useNotify();
    const { taskTypeLabels } = useConfigurationContext();

    const form = useForm<TaskFormValues>({
        defaultValues: {
            ...defaultValues,
            assignedMemberId: initialAssignedMemberId ?? null,
        },
    });

    /** Date-only (YYYY-MM-DD) from DB: use first 10 chars to avoid timezone shift when displaying */
    const dueDateForForm = (isoOrDateStr: string) =>
        isoOrDateStr?.slice(0, 10) ?? defaultValues.dueDate;

    useEffect(() => {
        if (mode === 'edit' && record) {
            form.reset({
                text: record.text ?? '',
                assignedMemberId: record.assignedMemberId ?? null,
                dueDate: record.dueDate
                    ? dueDateForForm(record.dueDate)
                    : defaultValues.dueDate,
                type: record.type ?? 'None',
            });
        } else {
            form.reset({
                ...defaultValues,
                assignedMemberId: initialAssignedMemberId ?? null,
            });
        }
    }, [mode, record, initialAssignedMemberId, form]);

    /** Store as UTC midnight for the given calendar day to avoid timezone shift */
    const transformDueDate = (dueDate: string) => {
        return new Date(dueDate + 'T00:00:00.000Z').toISOString();
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        form.handleSubmit(async (data: TaskFormValues) => {
            try {
                const input: TaskEditionInput = {
                    text: data.text,
                    assignedMemberId: data.assignedMemberId as MemberId | null,
                    dueDate: transformDueDate(data.dueDate),
                    type: data.type as TaskType,
                };

                if (mode === 'create') {
                    await createTask(input);
                } else {
                    if (!record?.id) {
                        notify('Record not found', { type: 'error' });
                        return;
                    }
                    await updateTask(record.id, input);
                }
                onSuccess();
            } catch (e) {
                notify(String(e), { type: 'error' });
            }
        })(event);
    };

    const handleDelete = async () => {
        if (!record?.id) return;
        try {
            await deleteTask(record.id);
            onSuccess();
        } catch (e) {
            notify(String(e), { type: 'error' });
        }
    };

    const saving = form.formState.isSubmitting;
    const canSave = !loading && !saving;

    return (
        <FormProvider {...form}>
            <RecordContextProvider value={record ?? undefined}>
                <form onSubmit={handleSubmit}>
                    <Stack gap={2}>
                        <TextInput
                            source="text"
                            label="Description"
                            validate={required()}
                            multiline
                            fullWidth
                            helperText={false}
                        />
                        <ReferenceInput
                            source="assignedMemberId"
                            reference="member"
                        >
                            <AutocompleteInput
                                label="Member"
                                optionText={memberOptionText}
                                inputText={memberInputText}
                                filterToQuery={search => ({ search })}
                                helperText={false}
                            />
                        </ReferenceInput>
                        <DateInput
                            source="dueDate"
                            validate={required()}
                            helperText={false}
                        />
                        <SelectInput
                            source="type"
                            validate={required()}
                            choices={taskTypeLabels.map(type => ({
                                id: type.value,
                                name: type.label,
                            }))}
                            helperText={false}
                        />
                    </Stack>
                    <Toolbar
                        sx={{ mt: 2, px: 0, justifyContent: 'space-between' }}
                    >
                        <Stack direction="row" gap={1}>
                            <MuiButton
                                variant="contained"
                                type="submit"
                                disabled={!canSave}
                                startIcon={<ContentSaveIcon />}
                            >
                                Save
                            </MuiButton>
                            <MuiButton
                                variant="text"
                                onClick={onClose}
                                disabled={saving}
                            >
                                Cancel
                            </MuiButton>
                        </Stack>
                        {mode === 'edit' && record?.id && (
                            <MuiButton
                                variant="text"
                                color="error"
                                disabled={saving}
                                onClick={() => {
                                    if (
                                        window.confirm(
                                            'Are you sure you want to delete this task?'
                                        )
                                    ) {
                                        handleDelete();
                                    }
                                }}
                            >
                                Delete
                            </MuiButton>
                        )}
                    </Toolbar>
                </form>
            </RecordContextProvider>
        </FormProvider>
    );
}
